import { html, markTrusted } from '../../ContentComposer/ContentComposer.js';
import { decodeHtmlEntities } from '../ModalUtils.js';

/**
 * TableHandle
 *
 * A live controller for a single table registered via addTable().
 * Stored in the builder's TableRegistry and returned via adapter.tables.get(id).
 *
 * Read properties:  id, header, columns, data, isShown
 * Mutation methods: setData, addRow, removeRow, updateRow, clearData, setHeader
 *
 * All mutation methods:
 *  - update the stored state immediately
 *  - patch the live DOM if the parent modal is currently shown
 *  - return `this` for fluent chaining
 */
export class TableHandle {
    /**
     * @param {string}              id         - The table element's id attribute
     * @param {string|object|null}  header     - Original header arg (string | config | null)
     * @param {Array}               columns    - Column definitions
     * @param {Array}               data       - Initial row data
     * @param {{ isShown: () => boolean }} ctx - Context supplied by the builder
     */
    constructor(id, header, columns, data, ctx) {
        this.id      = id;
        this.header  = header;
        this.columns = columns ? [...columns] : [];
        this.data    = data    ? [...data]    : [];
        this._ctx    = ctx;   // { isShown() }
    }

    // ── Read properties ───────────────────────────────────────────────────────

    /** True if the parent modal is currently open in the DOM. */
    get isShown() {
        return this._ctx.isShown();
    }

    // ── Mutation helpers ──────────────────────────────────────────────────────

    /**
     * Replace ALL rows in the table.
     * @param {Array} rows
     * @returns {TableHandle}
     */
    setData(rows) {
        this.data = Array.isArray(rows) ? [...rows] : [];
        if (this.isShown) this._patchTbody();
        return this;
    }

    /**
     * Append a single row.
     * @param {Array|object} row
     * @returns {TableHandle}
     */
    addRow(row) {
        this.data.push(row);
        if (this.isShown) this._patchTbody();
        return this;
    }

    /**
     * Remove a row by zero-based index.
     * @param {number} index
     * @returns {TableHandle}
     */
    removeRow(index) {
        if (index >= 0 && index < this.data.length) {
            this.data.splice(index, 1);
            if (this.isShown) this._patchTbody();
        }
        return this;
    }

    /**
     * Replace a row at a specific zero-based index.
     * @param {number}       index
     * @param {Array|object} row
     * @returns {TableHandle}
     */
    updateRow(index, row) {
        if (index >= 0 && index < this.data.length) {
            this.data[index] = row;
            if (this.isShown) this._patchTbody();
        }
        return this;
    }

    /**
     * Remove all rows, leaving an empty-state placeholder.
     * @returns {TableHandle}
     */
    clearData() {
        this.data = [];
        if (this.isShown) this._patchTbody();
        return this;
    }

    /**
     * Update the banner header title and/or styling.
     * @param {string|{ title?: string, background?: string, textColor?: string }} titleOrConfig
     * @returns {TableHandle}
     */
    setHeader(titleOrConfig) {
        this.header = titleOrConfig;
        if (this.isShown) this._patchHeader();
        return this;
    }

    // ── DOM patching ──────────────────────────────────────────────────────────

    /** Replace the <tbody> content for this table in the live DOM. */
    _patchTbody() {
        const tbody = document.querySelector(`#${this.id} tbody`);
        if (!tbody) return;
        tbody.innerHTML = this._buildTbodyHtml();
    }

    /** Replace or insert the banner <tr> in <thead> for this table. */
    _patchHeader() {
        const thead = document.querySelector(`#${this.id} thead`);
        if (!thead) return;

        // Remove existing banner row (first tr whose th has a colspan attr)
        const existing = thead.querySelector('tr th[colspan]');
        if (existing) existing.closest('tr').remove();

        if (!this.header) return; // nothing to insert

        const { titleText, bg, color } = this._resolveHeader();
        const colSpan = Math.max(1, this.columns.length);

        const bannerTr = document.createElement('tr');
        bannerTr.innerHTML =
            `<th colspan="${colSpan}" style="background:${bg};color:${color};` +
            `text-align:left;padding:8px 12px;font-weight:700;` +
            `text-transform:uppercase;font-size:0.85rem;border-bottom:none;">` +
            this._escapeForInnerHTML(titleText) +
            `</th>`;

        // Prepend before the column-header row
        thead.insertBefore(bannerTr, thead.firstChild);
    }

    // ── Internal rendering ────────────────────────────────────────────────────

    /** Resolve header arg to { titleText, bg, color }. */
    _resolveHeader() {
        const h = this.header;
        if (typeof h === 'string') {
            return { titleText: h, bg: '#0084ff', color: '#ffffff' };
        }
        if (h && typeof h === 'object') {
            return {
                titleText: h.title || h.text || '',
                bg:        h.background || h.bg || '#0084ff',
                color:     h.textColor  || h.color || '#ffffff'
            };
        }
        return { titleText: '', bg: '#0084ff', color: '#ffffff' };
    }

    /** Build the raw innerHTML string for <tbody>. */
    _buildTbodyHtml() {
        const rows = this.data;
        const cols = this.columns;
        const colSpan = Math.max(1, cols.length);

        if (!Array.isArray(rows) || rows.length === 0) {
            return `<tr><td colspan="${colSpan}" class="text-center text-muted">No data available</td></tr>`;
        }

        return rows.map(row => {
            if (Array.isArray(row)) {
                const tds = row.map(val => `<td>${this._renderCell(val)}</td>`).join('');
                return `<tr>${tds}</tr>`;
            } else if (row && typeof row === 'object') {
                const tds = cols.map(col => {
                    const key = typeof col === 'object' ? (col.data || col.title || '') : col;
                    const val = row[key] !== undefined ? row[key] : '';
                    return `<td>${this._renderCell(val)}</td>`;
                }).join('');
                return `<tr>${tds}</tr>`;
            }
            return '';
        }).join('');
    }

    /**
     * Render a single cell value.
     * Strings containing HTML tags pass through as-is (trusted by caller).
     * Everything else is entity-escaped.
     */
    _renderCell(val) {
        if (val === undefined || val === null) return '';
        const s = String(val);
        // If it contains HTML, treat as trusted markup (same policy as addTable)
        if (s.includes('<')) return s;
        return this._escapeForInnerHTML(s);
    }

    /** Minimal entity escape for safe innerHTML insertion. */
    _escapeForInnerHTML(str) {
        if (typeof str !== 'string') return String(str);
        return str.replace(/[&<>"']/g, m => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[m]));
    }
}
